import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import SolidGauge from "highcharts/modules/solid-gauge";
import HighchartsMore from "highcharts/highcharts-more";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "primereact/skeleton";
import "./gaugechart.scss";

import {
  selectFinancialHealthScoreSummary,
} from "../../../../../../rtk-query/features/dashboard/financialHealthScore/financialHealthScoreAnalysisSlice";

import { fhsGaugeStatus } from "../../../../../../utils/global";
import { getRangeCategory } from "../../../../../../utils/Utils";
import {
  setFinalGuageUpdate,
  setGuageIsLoading,
  setLoaderFalse,
} from "../../../../../../Redux/loaderSlice";

// init modules ONCE
HighchartsMore(Highcharts);
SolidGauge(Highcharts);

const GaugeChart = ({
  showCohort,
  maxValue = 100,
  cohortAverageValue = 0,
  graphHeight,
  graphPosition,
}) => {
  const dispatch = useDispatch();

  const finalGuageUpdate = useSelector(
    (state) => state?.loaderReducer?.finalGuageUpdate
  );

  const selectedCohort = useSelector(
    (state) => state?.cohortReducer?.selectedCohort
  );

  const bankPerformanceMetrics = useSelector(
    (state) =>
      state.infiniteBankPerformanceMetricsReducer?.selectedBankMetricData
  );

  const { payload: FHSSummary } = useSelector((state) =>
    selectFinancialHealthScoreSummary(state)
  );

  const FHSScore =
    bankPerformanceMetrics?.financial_health_score?.current ?? 0;

  const matchedStatus = useMemo(
    () =>
      fhsGaugeStatus.find(
        (item) => item.status === getRangeCategory(FHSScore)
      ),
    [FHSScore]
  );

  const [hoverState, setHoverState] = useState(null);

  const displayValue = hoverState?.value ?? FHSScore;
  const displayColor = hoverState?.color ?? "#6272D0";
  const displayLabel =
    hoverState?.label ?? matchedStatus?.description;

  /**
   * Handle loader flags once values ready
   */
  useEffect(() => {
    if (FHSScore == null) return;
    dispatch(setGuageIsLoading(false));
    setTimeout(() => {
      dispatch(setLoaderFalse());
      dispatch(setFinalGuageUpdate(true));
    }, 2000)
  }, [FHSScore, selectedCohort, cohortAverageValue]);

  /**
   * Stable Highcharts config
   */
  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "solidgauge",
        height: `${graphHeight}%`,
        backgroundColor: "none",
        animation: false, // 🔥 prevents flicker
      },

      title: null,

      pane: {
        startAngle: -90,
        endAngle: 89.9,
        center: graphPosition,
        size: "90%",
        background: [
          showCohort && {
            outerRadius: "175%",
            innerRadius: "160%",
            backgroundColor: "#B5F7EB",
            shape: "arc",
          },
          {
            outerRadius: "160%",
            innerRadius: "115%",
            backgroundColor: "#BBC3F3",
            shape: "arc",
          },
        ].filter(Boolean),
      },

      tooltip: { enabled: false },

      yAxis: {
        min: 0,
        max: maxValue,
        lineWidth: 0,
        tickPositions: [],
        labels: { enabled: false },
      },

      plotOptions: {
        series: {
          animation: false,
          point: {
            events: {
              mouseOver(e) {
                const isCohort = this.series.name === "Cohort";

                setHoverState({
                  value: Math.round(this.y),
                  color: this.color,
                  label: isCohort
                    ? "Cohort Average"
                    : FHSSummary?.status,
                });
              },
              mouseOut() {
                setHoverState(null);
              },
            },
          },
        },
      },

      series: [
        showCohort && {
          name: "Cohort",
          data: [
            {
              y: cohortAverageValue,
              color: "#56D8C0",
              radius: "175%",
              innerRadius: "160%",
            },
          ],
        },
        {
          name: "FHS",
          data: [
            {
              y: FHSScore,
              color: "#6272D0",
              radius: "160%",
              innerRadius: "115%",
            },
          ],
        },
      ].filter(Boolean),

      credits: { enabled: false },
    };
  }, [
    FHSScore,
    cohortAverageValue,
    showCohort,
    graphHeight,
    graphPosition,
    maxValue,
    FHSSummary,
  ]);

  return (
    <div className="gauge-wrapper">
      {FHSScore && finalGuageUpdate ? (
        <>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            immutable={false}
          />

          <div className="gauge-status-container">
            <div
              className="gauge-title"
              style={{
                color: displayColor,
                fontSize: "4rem",
                fontWeight: 600,
              }}
            >
              {displayValue}
            </div>

            {showCohort && (
              <div className="gauge-label-container mt-1">
                <div className="gauge-label w-50 d-inline-block">
                  <div className="dash-line fhs-square mx-2"></div>
                  <span className="fs-subdesc d-inline-block align-middle">
                    Your FI
                  </span>
                </div>
                <div className="gauge-label w-50 d-inline-block">
                  <div className="dash-line cohort-square mx-2"></div>
                  <span className="fs-subdesc d-inline-block align-middle">
                    Cohort
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="mx-0 d-flex flex-column justify-content-between rounded h-100 mb-10">
          <div className="guage-semicircle-container d-flex flex-column overflow-hidden">
            <div className="semicircle d-flex justify-content-center">
              <Skeleton
                shape="circle"
                size="29rem"
                className="skeleton-circle"
                style={{ zIndex: -1 }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GaugeChart;